<template>
  <div>
    <PositionWarning
      :show="showPositionWarning"
      :message="positionWarning"
      @dismiss="dismissWarning"
    />

    <FormGroupCharLimit
      label="Text"
      :value="text"
      :max="maxChars"
      max-editable
      @change="onChange('maxChar', $event)"
    >
      <template #default="{ state }">
        <b-textarea
          v-if="isMultiLineField"
          :value="text"
          :state="state"
          max-rows="8"
          @input="handleTextInput"
          @blur="onBlurText"
        />
        <b-input
          v-else
          :value="text"
          :state="state"
          @input="handleTextInput"
        />
        <b-checkbox
          v-model="titleCase"
          @change="toggleTitleCase"
        >
          Title Case
        </b-checkbox>
        <div
          v-if="isMultiLineField"
          class="d-flex align-items-baseline"
        >
          <div class="btn-group">
            <b-button
              class="align-to-btn mt-1 btn-sm"
              variant="primary"
              @click="redraw()"
            >
              <span v-if="target.alignToField">Align to <strong>{{ target.alignToField }}</strong></span>
              <span v-else>Select a field for text alignment</span>
            </b-button>
            
            <b-dropdown
              ref="alignToFieldDropdown"
              class="mt-1 align-options-dropdown"
              variant="primary"
              right
              no-caret
            >
              <template #button-content>
                <i class="fas fa-cog" />
              </template>
              <b-dropdown-form>
                <b-form-radio-group
                  v-model="shouldMaximizeFontSize"
                  class="mb-2"
                >
                  <b-form-radio :value="false">
                    Keep font size
                  </b-form-radio>
                  <b-form-radio :value="true">
                    Maximize font size
                  </b-form-radio>
                </b-form-radio-group>
                <b-form-checkbox
                  v-model="keepLineBreaks"
                >
                  Keep line breaks
                </b-form-checkbox>
                
                <b-form-checkbox
                  v-if="shouldMaximizeFontSize"
                  v-model="useTemplateFontSize"
                >
                  Use template font size as max
                </b-form-checkbox>
                <b-form-select 
                  :value="target.alignToField"
                  :options="alignToOptions"
                  size="sm" 
                  class="mt-3"
                  @change="onChange('changeAlignToField', $event)"
                />
              </b-dropdown-form>
            </b-dropdown>
          </div>

          <b-checkbox
            v-show="false"
            class="ml-1"
            :checked="getAutoPositionValue(target)"
            @change="onChangeAutoPositionValue($event)"
          >
            Auto position
          </b-checkbox>
        </div>
        <ExtendContainerRow
          v-if="isMultiLineField && options.extendContainer"
          class="mt-2"
          :target="target"
          :config="options.extendContainer"
          @data-updated="updateContainerSettings"
          @extend-container="onExtendContainer"
        />
      </template>
    </FormGroupCharLimit>

    <PositionAndSize
      :x="target.left"
      :y="target.top"
      :w="config.width"
      :h="config.height"
      @input="onChange"
    />
    <b-form-group
      v-if="themeStyles"
      label="Text Style"
    >
      <Multiselect
        v-model="selectedStyle"
        placeholder="Select style"
        track-by="name"
        label="name"
        :options="themeStyles"
        :allow-empty="false"
        @input="applyTextStyle"
      >
        <template #option="props">
          <span :style="getStylePreview(props.option)">{{ props.option.name }}</span>
        </template>
        <template #singleLabel="props">
          <span :style="getStylePreview(props.option)">{{ props.option.name }}</span>
        </template>
      </Multiselect>
    </b-form-group>

   
    <b-form-group
      v-if="options.fontSelector"
      label="Font"
    >
      <Multiselect
        v-model="fontVal"
        placeholder="Font"
        track-by="value"
        label="name"
        :options="getFonts"
        :allow-empty="false"
        @select="handleFontChange"
      >
        <template #option="props">
          <span :style="{ fontFamily: props.option.value }">{{ props.option.name }}</span>
        </template>
        <template #singleLabel="props">
          <span :style="{ fontFamily: props.option.value }">{{ props.option.name }}</span>
        </template>
      </Multiselect>
      <small 
        v-if="options.fontSelector && (!target.fontFamily || !fontVal)" 
        class="text-warning"
      >
        <i class="fas fa-exclamation-triangle mr-1" />
        Please select a font for this text field
      </small>
    </b-form-group>

    <b-form-group>
      <template #label>
        <div class="d-flex align-items-center justify-content-between">
          <span>Alignment</span>
          <img
            v-b-tooltip.hover
            src="/img/icons/text-fit-width.png"
            alt="Set field to text width"
            title="Set field to text width"
            style="height: 16px; cursor: pointer;"
            @mousedown.prevent
            @click="onFitTextWidthClick"
          >
        </div>
      </template>
     
 
      <b-form-select
        :value="target.textAlign"
        :options="alignments"
        @change="onChange('textAlignment', $event)"
      />
    </b-form-group>

    <b-form-row>
      <b-col>
        <b-form-group
          label="Size"
          :disabled="target.locked"
        >
          <b-form-input
            :value="config.fontSize"
            type="number"
            @input="onChange('fontSize', $event)"
            @dblclick="selectValueOnDblclick"
          />
        </b-form-group>
      </b-col>

      <b-col>
        <b-form-group label="Opacity">
          <b-form-input
            :value="target.opacity"
            type="number"
            min="0"
            step="0.01"
            max="1"
            @input="onChange('opacity', $event)"
            @dblclick="selectValueOnDblclick"
          />
        </b-form-group>
      </b-col>
    </b-form-row>

    <b-form-checkbox 
      :checked="target.isCurved"
      name="check-button" 
      switch
      @change="onChange('changeCurved', !target.isCurved)"
    >
      Curved
    </b-form-checkbox>
    <b-form-group 
      v-if="target.isCurved" 
      label=""
    >
      <b-input-group>
        <b-input
          min="300"
          max="3000"
          step="1"
          type="range"
          :value="target.curvedDiameter"
          @input="onChangeThrottle('changeRadius', $event)"
        />
        <template #append>
          <b-input
            min="0"
            max="1"
            step="0.01"
            type="number"
            :value="diameterToRatio(target.curvedDiameter)"
            @input="onRatioInput($event)"
          />
        </template>
      </b-input-group>
    </b-form-group>

    <b-form-row>
      <b-col>
        <b-form-group
          label="Related field"
        >
          <b-form-select
            :value="target.relatedField?.field"
            :options="relatedTemplateFields"
            @change="onChange('changeRelatedField', $event)"
          />
        </b-form-group>
      </b-col>

      <b-col>
        <b-form-group label="Margin">
          <b-form-input
            :value="target.relatedField?.margin"
            type="number"
            min="0"
            step="1"
            @input="onChange('changeMargin', $event)"
            @dblclick="selectValueOnDblclick"
          />
        </b-form-group>
      </b-col>
    </b-form-row>

    <div
      class="text-editor-actions d-flex my-2 justify-content-between"
    >
      <ColorPicker
        v-if="options.colorSelector"
        :value="target.fontColor"
        :themes="themes"
        position="top-start"
        copy
        @input="onChange('fontColor', $event)"
      />

      <DropdownCounter
        :value="target.charSpacing"
        title="Text Spacing"
        :step="10"
        @input="onChange('textSpacing', $event)"
      >
        <i class="glyphicon glyphicon-text-width" />
      </DropdownCounter>

      <div
        v-if="fontFamilyWeights.length"
        class="dropdown"
      >
        <button
          aria-expanded="false"
          aria-haspopup="true"
          class="dropdown-toggle d-flex align-items-center h-100"
          data-toggle="dropdown"
          title="Tracking"
          type="button"
        >
          <i class="glyphicon glyphicon glyphicon-bold" />
        </button>
        <div class="dropdown-menu menu-wide p-0 w-100">
          <button
            v-for="(font, index) in fontFamilyWeights"
            :key="index"
            :style="{ 'font-family': font.index }"
            class="dropdown-item p-2"
            type="button"
            @click="onChangeTextWeight(font.index)"
          >
            {{ TEXT_WEIGHT[font.weight] }}
          </button>
        </div>
      </div>
      <LineHeightDropdown
        :value="target.lineHeight"
        @input="onChange('textLeading', $event)"
      >
        <i class="cil-line-spacing" />
      </LineHeightDropdown>
      <button
        v-b-tooltip.hover
        title="Set to template width"
        @click="onChange('setTemplateSizeByWidth')"
      >
        <img src="/img/icons/expand.svg">
      </button>
      <button
        v-b-tooltip.hover
        title="Center to template width"
        @click="onChange('centerToTemplateWidth')"
      >
        <img
          src="/img/icons/align-center.png"
          alt="align-center-icon"
        >
      </button>
      <button
        v-b-tooltip.hover
        title="Left align to template"
        @click="onChange('leftAlignToTemplate')"
      >
        <img
          src="/img/icons/align-left.png"
          alt="align-left-icon"
        >
      </button>
      <button
        v-b-tooltip.hover
        title="Right align to template"
        @click="onChange('rightAlignToTemplate')"
      >
        <img
          src="/img/icons/align-right.png"
          alt="align-right-icon"
        >
      </button>
      <button
        v-b-tooltip.hover
        title="Set superscript"
        @click="onChange('superscript')"
      >
        <i class="fa fa-superscript" />
      </button>
      <button
        v-b-tooltip.hover
        title="Set subscript"
        @click="onChange('subscript')"
      >
        <i class="fa fa-subscript" />
      </button>
      <button
        v-b-tooltip.hover
        title="Set regular"
        @click="onChange('resetFontSize')"
      >
        <i class="fa fa-heading" />
      </button>
    </div>
    
    <ApplyToAllTextDropdown
      :options="options"
      :target="target"
    />

    <ChatGPTForm
      :value="target.text"
      class="flex-grow-1 d-flex mt-2"
      @change="onChange"
    />

    <b-button
      variant="danger"
      @click="onChange('delete')"
    >
      <b-icon icon="trash" />
      Delete
    </b-button>

    <LockToggle />
  </div>
</template>

<script>
/* global fontFamilyManager */
import ColorPicker from './components/ColorPicker.vue'
import {
  FIELD_TYPE_MULTI_LINE_TEXT,
} from '@frontend/constants/type-fields-of-template'
import { TEXT_WEIGHT } from '@frontend/constants/text-weight'
import DropdownCounter from './components/DropdownCounter.vue'
import { parseStyleToText } from '@/common/preview/parse-style-to-text'
import { editorControlEventBroker, EVENTS } from '@frontend/group/modules/editor-control-popup/event-broker'
import LineHeightDropdown from './components/LineHeightDropdown.vue'
import ChatGPTForm from './components/ChatGPTForm.vue'
import Multiselect from 'vue-multiselect'
import PositionAndSize from './components/PositionAndSize.vue'
import LockToggle from '@frontend/components/modules/layouts-page/EditorControl/components/LockToggle.vue'
import mixinInput from '@frontend/components/input-mixins'
import FormGroupCharLimit from '@frontend/components/common/atoms/FormGroupCharLimit.vue'
import ApplyToAllTextDropdown
  from '@frontend/components/modules/layouts-page/EditorControl/components/ApplyToAllTextDropdown.vue';
import { TEXT_ALIGNMENTS } from '@frontend/constants/alignment'
import { mapGetters } from 'vuex';
import { RELATED_FIELD_TYPES } from "@frontend/constants/related-fields";
import ExtendContainerRow from '@frontend/components/modules/layouts-page/EditorControl/components/ExtendContainerRow.vue';
import warningMixin from '@frontend/mixins/warningMixin';
import PositionWarning from '@frontend/components/shared/PositionWarning.vue'
import { ALIGN_TO_FIELD_TYPES } from "@frontend/constants/align-to-fields";
import {
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID,
  ADDITIONAL_RECTANGLE_PREFIX_ELEMENT_ID
} from "@frontend/constants/templates-field-prefixes";
import {ThemesContainerSingleton} from "@/banner_template/themes/ThemesContainerSinglton";
import {filterFontsByThemeFonts} from "@frontend/lib/utils/fonts";

export default {
  components: {
    ApplyToAllTextDropdown,
    FormGroupCharLimit,
    LockToggle,
    PositionAndSize,
    Multiselect,
    ColorPicker,
    DropdownCounter,
    LineHeightDropdown,
    ChatGPTForm,
    ExtendContainerRow,
    PositionWarning,
  },
  mixins: [mixinInput, warningMixin],
  props: {
    target: {
      type: fabric.Object,
      require: true,
      default: () => {},
    },
    options: {
      type: Object,
      default: () => {},
    },
    themes: {
      type: Array,
      default: () => [],
    },
    fieldType: {
      type: String,
      require: true,
      default: '',
    },
  },
  data () {
    return {
      config: {width: 0, height: 0, fontSize: 0},
      TEXT_WEIGHT,
      chatGPTInput: '',
      isPinProperties: false,
      isApplyToAllUpdate: true,
      isApplyToAllAdd: false,
      fontFamily: [],
      alignments: TEXT_ALIGNMENTS,
      fontFamilyWeights: [],
      selectedFontFamily: '',
      titleCase: false,
      shouldMaximizeFontSize: false,
      keepLineBreaks: true,
      useTemplateFontSize: false,
      fontVal: '',
      selectedStyle: null
    }
  },
  computed: {
    ...mapGetters('layout', [
      'selectedInstance',
    ]),
    text () {
      return parseStyleToText(this.target.textLines, this.target.styles)
    },
    isMultiLineField () {
      return [FIELD_TYPE_MULTI_LINE_TEXT].includes(this.fieldType)
    },
    getFonts () {
      const fonts = []
      const theme = ThemesContainerSingleton.getByIdSync(this.selectedInstance.data.theme);
      const allFonts = theme
        ? filterFontsByThemeFonts(this.options.fonts, theme, [this.target.fontFamily])
        : this.options.fonts;

      for (const key in allFonts) {
        const font = allFonts[key]
        const selectedFontFamily = this.target.fontFamily

        if (Array.isArray(font)) {
          fonts.push({
            name: key,
            value: font.includes(selectedFontFamily) ? selectedFontFamily : font[0],
          })
        } else {
          fonts.push({
            name: font,
            value: key,
          })
        }
      }

      return fonts
    },
    themeStyles() {
      const theme = ThemesContainerSingleton.getByIdSync(this.selectedInstance.data.theme);
      let styles = theme?.attributes?.find(attr => _.get(attr, 'name') === 'Styles')?.list || [];
      if (!styles?.length && window.selectedTheme) {
        styles = window.selectedTheme?.attributes?.find(attr => _.get(attr, 'name') === 'Styles')?.list || [];
      }
      return styles;   
    },
    maxChars() {
      return this.target.maxChars || 10000;
    },
    relatedTemplateFields() {
      const fields = this.selectedInstance.template.fields
        .filter(field => RELATED_FIELD_TYPES.includes(field.type) && field.name !== this.target.templateField?.name)
        .map(field => field.name);
      
      return [
        { value: '', text: 'Select related field' },
        ...fields
      ];
    },
    alignToOptions() {
      const canvasObjects = this.selectedInstance.canvas.getObjects()
        .map(object => object.templateField?.name)
        .filter(object => object);
      
      const fields = this.selectedInstance.template.fields
        .filter(field => ALIGN_TO_FIELD_TYPES.includes(field.type) && canvasObjects.includes(field.name))
        .map(field => field.name);
      
      const additionalRectangles = this.selectedInstance.canvas.getObjects()
        .filter(object => object.element_id?.startsWith(ADDITIONAL_RECTANGLE_PREFIX_ELEMENT_ID) || object.id?.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID))
        .map(field => field.name);
      
      return [
        { value: '', text: 'Select a field for text alignment' },
        ...fields.concat(additionalRectangles)
      ];
    },
   
  },
  watch: {
    'target'() {
      this.fontVal = this.findFont(this.target.fontFamily);
    },
    themeStyles: {
      immediate: true,
      handler() {
        this.matchSelectedStyle();
      }
    }
  },
  mounted() {
    this.config = {
      width: this.target.width,
      height: this.target.height,
      fontSize: this.target.fontSize,
    };

    this.autoPosition = !!this.target?.autoPosition
    this.fontVal = this.findFont(this.target.fontFamily);


    // for style matching
    this.matchSelectedStyle();

    editorControlEventBroker.on(EVENTS.TEXT_SELECTION_CHANGED, ({ fontFamily }) => {
      this.fontVal = this.findFont(fontFamily);
    });
   
  },
  methods: {
    onChangeTextWeight (fontFamily) {
      this.onChange('fontFamily', fontFamily)
    },
    setFontFamily (fontFamily) {
      const fontFamilies = fontFamilyManager.getFontsFamily(fontFamily)
      if (fontFamilies) {
        this.fontFamilyWeights = fontFamilies.fonts
      } else {
        this.fontFamilyWeights = []
      }
    },
    diameterToRatio(diameter) {
      const min = 300;
      const max = 3000;
      return ((diameter - min) / (max - min)).toFixed(2);
    },
    ratioToDiameter(ratio) {
      const min = 300;
      const max = 3000;
      return Math.round(min + ratio * (max - min));
    },
    onRatioInput(event) {
      const ratio = parseFloat(event);
      if (isNaN(ratio)) return;
      const diameter = this.ratioToDiameter(ratio);
      this.onChangeThrottle('changeRadius', diameter);
    },
    onChangeThrottle: _.throttle(function (action, value) {
      this.onChange(action, value)
    }, 200),
    onChange (action, value) {
      editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
        action,
        value,
        maximizeFontSize: this.shouldMaximizeFontSize,
        keepLineBreaks: this.keepLineBreaks,
        useTemplateFontSize: this.useTemplateFontSize,
      })
      if (['fontFamily', 'fontSize', 'fontColor'].includes(action)) {
        this.matchSelectedStyle();
      }
    },
    onBlurText(event) {
      // do not trigger alignToFieldDropdown.show() when clicking Templates and Properties dropdown
      if (event.relatedTarget && event.relatedTarget.innerText.match(/(All templates)|(Replace Text)/)) {
        this.onChange('redrawText', '')
        return
      }
      this.redraw();
    },
    redraw() {
      if (!this.target.alignToField) {
        this.$refs.alignToFieldDropdown.show();
        
        return;
      }
      
      this.onChange('redrawText', '')
      
    },
    update({width, height, fontSize}) {
      this.config = {width, height, fontSize};
    },
    getAutoPositionValue(target) {
      if (Object.hasOwn(this.selectedInstance.data, `${target.element_id}_auto_position`)) {
        const value = this.selectedInstance.data[`${target.element_id}_auto_position`];
        return value === 'true' || value === true;
      }

      return false;
    },
    onChangeAutoPositionValue(value) {
      this.onChange('autoPosition', value);
    },
    toTitleCase (str) {
      return str.replace(/\w\S*/g, function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      })
    },
    handleTextInput (value) {
      let newValue = value
      if (this.titleCase) {
        newValue = this.toTitleCase(value)
      }
      this.onChange('inputText', newValue)
    },
    toggleTitleCase () {
      if (this.titleCase) {
        const updatedText = this.toTitleCase(this.text)
        this.onChange('inputText', updatedText)
      }
    },
    updateContainerSettings(event) {
      editorControlEventBroker.fire(EVENTS.EXTEND_CONTAINER_SETTINGS_CHANGED, {target: this.target, value: event});
    },
    onExtendContainer(event) {
      editorControlEventBroker.fire(EVENTS.EXTEND_CONTAINER, event);
    },
    handleFontChange(e) {
      this.setFontFamily(e.value);
      this.onChange('fontFamily', e.value);
    },
    findFont(value) {
      return _.find(this.getFonts, ['value', value]);
    },
    onFitTextWidthClick() {
      editorControlEventBroker.fire(EVENTS.FIT_TEXT_WIDTH, {
        object: this.target
      })
    },
    applyTextStyle(style) {
      if (!style) return;
      style.list.forEach(attr => {
        if (attr.type === 'font') {
          const newFont = this.findFont(attr.value);
          if (!newFont) {
            toastr.warning('Font not found, please check theme Styles And Font settings');
            return;
          }
          this.fontVal = newFont;
          this.setFontFamily(newFont.value);
          this.onChange('fontFamily', newFont.value);
        } else if (attr.type === 'number') {
          // fallback if font size is not set
          const newSize = parseInt(attr.value) > 0 ? parseInt(attr.value) : 16;
          this.onChange('fontSize', newSize);
        } else if (attr.type === 'color') {
          // if color is not set
          const color = attr.value || '#000';
          this.onChange('fontColor', color);
        }
      });
    },
    getStylePreview(style) {
      const font = _.get(style.list.find(attr => attr.type === 'font'), 'value') || 'inherit';
      const color = _.get(style.list.find(attr => attr.type === 'color'), 'value') || '#000';

      return {
        fontFamily: font,
        color: color
      };
    },

    matchSelectedStyle() {
      if (!this.themeStyles || this.themeStyles.length === 0) return;

      const matchedStyle = this.themeStyles.find(style => {
        const font = _.get(style.list.find(attr => attr.type === 'font'), 'value');
        const size = parseInt(_.get(style.list.find(attr => attr.type === 'number'), 'value'));
        const color = _.get(style.list.find(attr => attr.type === 'color'), 'value').toLowerCase();
        const matchesFont = font === this.target.fontFamily;
        const matchesSize = size === parseInt(this.target.fontSize);
        const matchesColor = !color || color === this.target.fontColor?.toLowerCase();
        
        return matchesFont && matchesSize && matchesColor;
      });

      if (matchedStyle) {
        this.selectedStyle = matchedStyle;
      } else {
        this.selectedStyle = null;
      }
    }
  }
}
</script>

<style scoped lang="scss">
.btn-sm {
  min-width: 120px;
}

.align-to-btn {
  min-width: 200px;
}

.align-options-dropdown {
  ::v-deep .dropdown-toggle {
    border-left: 1px solid rgba(255, 255, 255, 0.3);
    padding: 0.25rem 0.5rem;
    
    &::after {
      display: none;
    }
  }
  
  ::v-deep .dropdown-menu {
    min-width: 200px;
    padding: 0.5rem;
  }
}

.btn-group {
  .btn:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  
  .dropdown-toggle {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}

.b-dropdown-form{
  padding: .5rem 1rem;
}

.position-warning {
  font-size: 0.9rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  
  .warning-actions {
    .btn-sm {
      display: flex !important;
      align-items: center !important;
      min-width: auto;
      padding: 0.25rem 0.75rem;
      color: #000 !important;
    }
  }
}
</style>
